import { Suspense, useCallback, useEffect, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { Link, Navigate, NavLink, Outlet, useLocation, useNavigate } from "react-router"
import { ChevronDown, LogOut, X } from "lucide-react"
import { BrandLogo } from "@web/components/BrandLogo"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@web/components/ui/dropdown-menu"
import { cn } from "@web/lib/utils"
import type {
  AdminShellContext,
  BreadcrumbItem as BreadcrumbItemType,
} from "../hooks/useHeaderBreadcrumbs"
import { PageLoader } from "./PageLoader"
import { SwipeBack } from "./SwipeBack"
import { apiFetch } from "../lib/api"
import { authClient, useStaffMe } from "../lib/auth"

type NavLinkItem = { href: string; text: string; end?: boolean; badge?: number }
type NavMenu = { label: string; items: (NavLinkItem | "separator")[] }
type NavEntry = NavLinkItem | NavMenu

function useOpenIssuesCount(enabled: boolean) {
  return useQuery({
    enabled,
    queryKey: ["dish-issues", "open-count"],
    queryFn: async () => {
      const res = await apiFetch<{ counts: { open: number } }>(
        "/api/admin/dish-issues?status=open&page=1",
      )
      return res.counts.open
    },
    refetchInterval: 60_000,
  })
}

function useNav(isStaff: boolean): NavEntry[] {
  const session = authClient.useSession()
  const isOwner = session.data?.user?.role === "owner"
  const openIssues = useOpenIssuesCount(true)

  if (isStaff) {
    return [
      { href: "/admin/dashboard", text: "Дашборд" },
      { href: "/admin/orders", text: "Заказы" },
      { href: "/admin/customers", text: "Клиенты" },
      { href: "/admin/issues", text: "Ошибки", badge: openIssues.data || undefined },
    ]
  }

  return [
    { href: "/admin/orders", text: "Заказы" },
    { href: "/admin/customers", text: "Клиенты" },
    { href: "/admin/issues", text: "Ошибки", badge: openIssues.data || undefined },
    { href: "/admin/nomenclature", text: "Номенклатура" },
    {
      label: "Документы",
      items: [
        { href: "/admin/documents", text: "Создать документ", end: true },
        { href: "/admin/documents/history", text: "История" },
        { href: "/admin/documents/mappings", text: "Маппинги" },
        { href: "/admin/documents/reference", text: "Справочники" },
      ],
    },
    {
      label: "Ещё",
      items: [
        { href: "/admin/channels", text: "Уведомления" },
        { href: "/admin/telegram-bots", text: "Telegram боты" },
        { href: "/admin/sales-points", text: "Точки продаж" },
        ...(isOwner ? [{ href: "/admin/users", text: "Сотрудники" }] : []),
        ...(isOwner ? [{ href: "/admin/api-tokens", text: "API токены" }] : []),
        "separator" as const,
        { href: "/admin/jobs", text: "Jobs" },
      ],
    },
  ]
}

function isActivePath(pathname: string, item: NavLinkItem) {
  return item.end
    ? pathname === item.href
    : pathname === item.href || pathname.startsWith(item.href + "/")
}

const navText = "text-xs font-semibold uppercase tracking-[0.1em]"

function IssueBadge({ count }: { count: number }) {
  return (
    <span className="ml-2 inline-flex min-w-4 items-center justify-center bg-signal px-1 py-0.5 text-[10px] font-semibold leading-none text-white">
      {count}
    </span>
  )
}

function DesktopNav({ nav, pathname }: { nav: NavEntry[]; pathname: string }) {
  return (
    <nav aria-label="Основное меню" className="hidden min-w-0 flex-1 items-stretch gap-7 xl:flex">
      {nav.map((entry) => {
        if ("href" in entry) {
          const active = isActivePath(pathname, entry)
          return (
            <Link
              key={entry.href}
              to={entry.href}
              aria-current={active ? "page" : undefined}
              className={cn(
                "flex h-16 items-center border-b-2 pt-0.5 transition-colors",
                navText,
                active
                  ? "border-foreground text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {entry.text}
              {entry.badge != null && <IssueBadge count={entry.badge} />}
            </Link>
          )
        }
        const active = entry.items.some((i) => i !== "separator" && isActivePath(pathname, i))
        return (
          <DropdownMenu key={entry.label}>
            <DropdownMenuTrigger
              className={cn(
                "flex h-16 cursor-pointer items-center gap-1.5 border-b-2 pt-0.5 outline-none transition-colors",
                navText,
                active
                  ? "border-foreground text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground data-[state=open]:text-foreground",
              )}
            >
              {entry.label}
              <ChevronDown className="size-3" strokeWidth={2} aria-hidden="true" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-48">
              {entry.items.map((item, idx) =>
                item === "separator" ? (
                  <DropdownMenuSeparator key={`sep-${idx}`} />
                ) : (
                  <DropdownMenuItem key={item.href} asChild>
                    <Link
                      to={item.href}
                      className={cn(isActivePath(pathname, item) && "font-semibold")}
                    >
                      {item.text}
                    </Link>
                  </DropdownMenuItem>
                ),
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )
      })}
    </nav>
  )
}

type ShellUser = {
  name?: string | null
  email: string
  role?: string | null
  /** Явная подпись вместо роли (сотрудник из чата: «Сотрудник · доступ до 21:30»). */
  roleLabel?: string
}

const ROLE_LABEL: Record<string, string> = { owner: "Владелец", admin: "Администратор" }

function initials(user: ShellUser): string {
  const words = (user.name ?? "").trim().split(/\s+/).filter(Boolean)
  const letters = words.length > 0 ? words.slice(0, 2).map((w) => w[0]) : [user.email[0] ?? "?"]
  return letters.join("").toUpperCase()
}

function UserAvatar({ user, className }: { user: ShellUser; className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "flex size-8 shrink-0 items-center justify-center rounded-full bg-accent text-[11px] font-semibold tracking-[0.04em] text-foreground ring-1 ring-border",
        className,
      )}
    >
      {initials(user)}
    </span>
  )
}

function UserIdentity({ user }: { user: ShellUser }) {
  const role = user.roleLabel ?? (user.role ? ROLE_LABEL[user.role] : undefined)
  return (
    <span className="flex min-w-0 flex-col gap-0.5">
      <span className="font-heading text-lg leading-tight [overflow-wrap:anywhere]">
        {user.name || user.email}
      </span>
      {user.name && user.email ? (
        <span className="break-all text-xs text-muted-foreground">{user.email}</span>
      ) : null}
      {role ? (
        <span className="pt-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
          {role}
        </span>
      ) : null}
    </span>
  )
}

function UserMenu({
  user,
  onLogout,
  showLogout,
}: {
  user: ShellUser
  onLogout: () => void
  showLogout: boolean
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Меню пользователя"
          className="hidden h-11 cursor-pointer items-center gap-2.5 text-[13px] text-[#44403C] outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/30 data-[state=open]:text-foreground xl:flex"
        >
          <UserAvatar user={user} />
          <span className="max-w-44 truncate">{user.name || user.email}</span>
          <ChevronDown className="size-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className="w-72">
        <DropdownMenuLabel className="flex items-center gap-3 px-3 py-3 text-sm font-normal normal-case tracking-normal text-foreground">
          <UserAvatar user={user} className="size-10 text-xs" />
          <UserIdentity user={user} />
        </DropdownMenuLabel>
        {showLogout ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={onLogout} className="cursor-pointer px-3 py-2.5">
              <LogOut className="size-4" />
              Выйти
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function MobileNav({
  nav,
  open,
  onClose,
  onLogout,
  showLogout,
  user,
}: {
  nav: NavEntry[]
  open: boolean
  onClose: () => void
  onLogout: () => void
  showLogout: boolean
  user: ShellUser | undefined
}) {
  const main = nav.filter((e): e is NavLinkItem => "href" in e)
  const menus = nav.filter((e): e is NavMenu => "items" in e)
  const linkClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      "flex min-h-11 items-center px-6 text-sm font-semibold uppercase tracking-[0.1em] transition-colors",
      isActive ? "bg-accent text-foreground" : "text-muted-foreground hover:bg-accent",
    )
  const groupLabel =
    "px-6 pb-1 pt-5 text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground/70"
  return (
    <>
      {open ? (
        <button
          type="button"
          aria-label="Закрыть меню"
          onClick={onClose}
          className="fixed inset-0 z-30 bg-black/40 xl:hidden"
        />
      ) : null}
      <aside
        aria-hidden={!open}
        className={cn(
          "fixed inset-y-0 right-0 z-40 flex w-72 max-w-[85vw] flex-col overflow-y-auto border-l bg-card transition-transform xl:hidden",
          open ? "translate-x-0" : "invisible translate-x-full",
        )}
      >
        <div className="flex h-[60px] shrink-0 items-center justify-end border-b px-4">
          <button
            type="button"
            aria-label="Закрыть меню"
            onClick={onClose}
            className="-mr-2.5 flex size-11 items-center justify-center"
          >
            <X className="size-5" />
          </button>
        </div>
        <nav className="flex flex-col pb-6">
          <div className={groupLabel}>Основное</div>
          {main.map((item) => (
            <NavLink
              key={item.href}
              to={item.href}
              end={item.end}
              onClick={onClose}
              className={linkClass}
            >
              {item.text}
              {item.badge != null && <IssueBadge count={item.badge} />}
            </NavLink>
          ))}
          {menus.map((menu) => (
            <div key={menu.label} className="flex flex-col">
              <div className={groupLabel}>{menu.label}</div>
              {menu.items.map((item, idx) =>
                item === "separator" ? (
                  <div key={`sep-${idx}`} className="mx-6 my-1 border-t" />
                ) : (
                  <NavLink
                    key={item.href}
                    to={item.href}
                    end={item.end}
                    onClick={onClose}
                    className={linkClass}
                  >
                    {item.text}
                  </NavLink>
                ),
              )}
            </div>
          ))}
        </nav>
        {user ? (
          <div className="mt-auto flex flex-col gap-3 border-t px-6 py-5">
            <div className="flex items-center gap-3">
              <UserAvatar user={user} className="size-10 text-xs" />
              <UserIdentity user={user} />
            </div>
            {showLogout ? (
              <button
                type="button"
                onClick={onLogout}
                className="flex min-h-11 items-center justify-center gap-2 border text-xs font-semibold uppercase tracking-[0.1em] hover:bg-accent"
              >
                <LogOut className="size-4" />
                Выйти
              </button>
            ) : null}
          </div>
        ) : null}
      </aside>
    </>
  )
}

export function AdminShell() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItemType[] | null>(null)
  const navigate = useNavigate()
  const session = authClient.useSession()
  const staffMe = useStaffMe(!session.isPending && !session.data?.user)
  const staff = !session.data?.user ? staffMe.data : null
  const isStaff = !!staff
  const homePath = "/admin/dashboard"
  const nav = useNav(isStaff)
  const stableSetBreadcrumbs = useCallback(
    (items: BreadcrumbItemType[] | null) => setBreadcrumbs(items),
    [],
  )
  const outletContext: AdminShellContext = { setBreadcrumbs: stableSetBreadcrumbs }
  const location = useLocation()
  const isTelegramMiniApp = !!window.Telegram?.WebApp?.initData
  const isHome = location.pathname === homePath
  // Одна цель «назад» для кнопки Telegram и свайпа от края.
  const goBack = useCallback(
    () => navigate(breadcrumbs?.[0]?.to ?? homePath),
    [navigate, breadcrumbs, homePath],
  )

  useEffect(() => {
    const tg = window.Telegram?.WebApp
    if (!tg?.BackButton) return

    try {
      const isDashboard = location.pathname === homePath
      if (isDashboard) {
        tg.BackButton.hide()
        return
      }

      tg.BackButton.show()
      const handler = goBack
      tg.BackButton.onClick(handler)
      return () => {
        tg.BackButton.offClick(handler)
        tg.BackButton.hide()
      }
    } catch {}
  }, [location.pathname, goBack, homePath])

  const handleLogout = async () => {
    await authClient.signOut()
    navigate("/login", { replace: true })
  }

  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  const staffUntil = staff?.expiresAt
    ? new Date(staff.expiresAt).toLocaleTimeString("ru-RU", {
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "Europe/Moscow",
      })
    : null
  const user: ShellUser | undefined = session.data?.user
    ? session.data.user
    : staff
      ? {
          name: staff.telegramName,
          email: "",
          roleLabel: staffUntil ? `Сотрудник · доступ до ${staffUntil}` : "Сотрудник",
        }
      : undefined
  const showLogout = !!session.data?.user && !isTelegramMiniApp

  // Сотруднику доступны только дашборд, заказы, клиенты и ошибки (чтение).
  if (
    isStaff &&
    !/^\/admin\/((dashboard|orders|customers|issues)(\/|$)|sales-points\/[^/]+$)/.test(
      location.pathname,
    )
  ) {
    return <Navigate to="/admin/dashboard" replace />
  }

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-background">
      <header className="flex h-[60px] shrink-0 items-center gap-10 border-b border-border bg-card px-4 md:h-16 md:px-10">
        <Link to={homePath} aria-label="Ассистент Dobraw" className="shrink-0">
          <BrandLogo />
        </Link>
        <DesktopNav nav={nav} pathname={location.pathname} />
        <div className="ml-auto flex items-center gap-5 text-[13px]">
          {user ? <UserMenu user={user} onLogout={handleLogout} showLogout={showLogout} /> : null}
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Меню"
            className="-mr-2.5 flex size-11 items-center justify-center md:mr-0 xl:hidden"
          >
            <svg
              aria-hidden="true"
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="square"
            >
              <path d="M4 7h16M4 12h16M4 17h16" />
            </svg>
          </button>
        </div>
      </header>

      <SwipeBack enabled={isTelegramMiniApp && !isHome && !mobileOpen} onBack={goBack} />
      <MobileNav
        nav={nav}
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        onLogout={handleLogout}
        showLogout={showLogout}
        user={user}
      />

      <main className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-background">
        <Suspense fallback={<PageLoader />}>
          <Outlet context={outletContext} />
        </Suspense>
      </main>
    </div>
  )
}
